import React from 'react';
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdErrorOutline,
  MdInfoOutline,
  MdWarningAmber,
} from 'react-icons/md';
import type { GlassNotificationTone } from './glassNotificationTokens';

const TONE_CONFIG: Record<
  GlassNotificationTone,
  { IconEl: typeof MdCheckCircle | 'spinner'; lightIcon: string; darkIcon: string }
> = {
  success: { IconEl: MdCheckCircle, lightIcon: 'green.500', darkIcon: 'green.300' },
  error: { IconEl: MdErrorOutline, lightIcon: 'red.500', darkIcon: 'red.300' },
  warning: { IconEl: MdWarningAmber, lightIcon: 'orange.500', darkIcon: 'orange.300' },
  info: { IconEl: MdInfoOutline, lightIcon: 'blue.500', darkIcon: 'cyan.300' },
  loading: { IconEl: 'spinner', lightIcon: 'purple.500', darkIcon: 'purple.300' },
};

export interface GlassNotificationSurfaceProps {
  tone: GlassNotificationTone;
  title: React.ReactNode;
  description?: React.ReactNode;
  onClose?: () => void;
  isClosable?: boolean;
  customIcon?: React.ReactNode;
}

/**
 * Superficie compartida para toasts y avisos inline, alineada con Mi Perfil.
 */
export function GlassNotificationSurface({
  tone,
  title,
  description,
  onClose,
  isClosable = false,
  customIcon,
}: GlassNotificationSurfaceProps) {
  const cfg = TONE_CONFIG[tone];
  const iconColor = useColorModeValue(cfg.lightIcon, cfg.darkIcon);

  const panelBg = useColorModeValue(
    'rgba(255, 255, 255, 0.9)',
    'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(8, 15, 28, 0.94) 100%)'
  );
  const panelBorder = useColorModeValue('rgba(0, 0, 0, 0.06)', 'rgba(148, 163, 184, 0.2)');
  const borderAccent = useColorModeValue(
    {
      success: 'rgba(16, 185, 129, 0.72)',
      error: 'rgba(239, 68, 68, 0.72)',
      warning: 'rgba(245, 158, 11, 0.76)',
      info: 'rgba(59, 130, 246, 0.72)',
      loading: 'rgba(139, 92, 246, 0.72)',
    }[tone],
    {
      success: 'rgba(52, 211, 153, 0.66)',
      error: 'rgba(248, 113, 113, 0.66)',
      warning: 'rgba(251, 191, 36, 0.68)',
      info: 'rgba(96, 165, 250, 0.66)',
      loading: 'rgba(167, 139, 250, 0.66)',
    }[tone]
  );
  const shadow = useColorModeValue(
    '0 10px 40px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.92)',
    '0 14px 48px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.1)'
  );
  const titleColor = useColorModeValue('gray.800', 'white');
  const descColor = useColorModeValue('gray.600', 'whiteAlpha.850');
  const iconTileBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const iconTileBorder = useColorModeValue('blackAlpha.100', 'whiteAlpha.150');
  const closeHoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  return (
    <Box position="relative" borderRadius="2xl" overflow="hidden" role="status">
      <Flex
        position="relative"
        align="flex-start"
        gap={3}
        borderRadius="2xl"
        border="1px solid"
        borderColor={panelBorder}
        borderLeftWidth="4px"
        borderLeftColor={borderAccent}
        bg={panelBg}
        backdropFilter="blur(20px) saturate(180%)"
        sx={{
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: shadow,
        }}
        py={{ base: 3, md: 4 }}
        px={{ base: 3, md: 4 }}
        pr={isClosable ? { base: 11, md: 12 } : { base: 3, md: 4 }}
      >
        <Flex
          flexShrink={0}
          w={{ base: '34px', md: '38px' }}
          h={{ base: '34px', md: '38px' }}
          borderRadius="12px"
          align="center"
          justify="center"
          bg={iconTileBg}
          border="1px solid"
          borderColor={iconTileBorder}
          boxShadow="inset 0 1px 0 rgba(255,255,255,0.45)"
        >
          {customIcon ? (
            <Box color={iconColor}>{customIcon}</Box>
          ) : cfg.IconEl === 'spinner' ? (
            <Spinner
              thickness="2.5px"
              speed="0.75s"
              emptyColor="transparent"
              color={iconColor}
              size="sm"
              aria-label="Cargando"
            />
          ) : (
            <Icon as={cfg.IconEl} boxSize={{ base: 5, md: 5 }} color={iconColor} />
          )}
        </Flex>
        <Box flex="1" minW={0} pt={0.5}>
          <Text
            as="div"
            fontSize={{ base: 'sm', md: 'md' }}
            fontWeight="semibold"
            letterSpacing="-0.01em"
            color={titleColor}
            mb={description ? 1 : 0}
            lineHeight="short"
          >
            {title}
          </Text>
          {description ? (
            <Text
              as="div"
              fontSize={{ base: 'xs', md: 'sm' }}
              color={descColor}
              lineHeight="tall"
              opacity={0.98}
            >
              {description}
            </Text>
          ) : null}
        </Box>
        {isClosable && onClose ? (
          <CloseButton
            position="absolute"
            right={2}
            top={2}
            size="md"
            borderRadius="lg"
            _hover={{ bg: closeHoverBg }}
            onClick={onClose}
            aria-label="Cerrar notificación"
          />
        ) : null}
      </Flex>
    </Box>
  );
}
